import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { isNullOrUndefined } from 'Util/TypeGuards';
import AmountPayable from 'Views/Components/_HumanWritten/Payment/AmountPayable';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import PaymentTypeSelection from 'Views/Components/_HumanWritten/Payment/PaymentTypeSelection';
import { usePaymentAmount } from './usePaymentAmount';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { store } from 'Models/Store';
import PaymentProviderWrapper from './PaymentProviderWrapper';
import { appConfigStore } from 'Models/AppConfigStore';
import paymentStore from 'Models/PaymentStore';
import { SandboxBadge } from './SandboxPaymentHelpers';
import { useTransactionHasOnlyNewBookings } from 'Services/CustomGql/TransactionEntityType';
import { useQuery } from '@tanstack/react-query';
import { fetchUserById } from 'Services/Api/_HumanWritten/UserService';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export type PaymentMethodId =
	| 'credit-card-option'
	| 'cash-option'
	| 'invoice-option'
	| 'pay-later'
	| 'eftpos-option'
	| 'terminal-option'
	| string;
export type PaymentLocation =
	| 'booking-wizard'
	| 'check-in'
	| 'gift-certificate'
	| 'anytime-ticket'
	| 'parking'
	| 'complete-payment'
	| 'charter';

/** Non-card payment option IDs. Anything not in this list is treated as a saved card identifier. */
export const KNOWN_NON_CARD_OPTIONS: PaymentMethodId[] = [
	'credit-card-option',
	'cash-option',
	'invoice-option',
	'pay-later',
	'eftpos-option',
	'terminal-option',
];

export interface PaymentTypeWrapperProps {}

function PaymentTypeWrapper() {
	const [canInvoice, setCanInvoice] = useState(false);
	const [canPayLater, setCanPayLater] = useState(false);
	const [canTakeCash, setCanTakeCash] = useState(false);
	const [canPayByTerminal, setCanPayByTerminal] = useState(false);
	const [canPayByCard, setCanPayByCard] = useState(true);
	const [invoiceDisabledForPaidBooking, setInvoiceDisabledForPaidBooking] = useState(false);
	const {
		setTotalPrice,
		setPaymentMethod,
		setDynamicSurcharge,
		setDisableContinueButton,
		transactionId,
		userId,
		isAlteration,
		tripType,
		paymentLocation,
		paymentMethod,
		includeSubmitButtons,
		canInvoiceOverride,
		canPayByCardOverride,
		eventBooking,
		totalPrice,
		dynamicSurcharge,
		triggerRecalculation,
		message,
		showSurchargeModal,
		paymentProcessing,
		onCancelRedirectUrl,
		transactionDetails,
		inCheckIn,
	} = usePaymentCheckOut();

	// Undefined until known.
	const canSkipPayment = useTransactionHasOnlyNewBookings(transactionId);
	// The booked-for customer (self-booking → themselves, on-behalf → the customer). Whether their
	// type is invoiced drives the invoice-first ordering + Send invoice pre-select below.
	const { data: bookedForCustomer } = useQuery({
		queryKey: ['user', userId],
		queryFn: () => fetchUserById(userId!),
		enabled: !!userId,
	});
	// Derive "invoiced" from the per-type payment options (Invoice present) — the same data that
	// drives canInvoice — rather than the derived invoicedUser flag, which is nullable and only
	// recomputed on save, so a type that includes Invoice but hasn't been re-saved reads as null.
	// Fall back to the flag only when the type has no explicit option set.
	const customerPaymentOptions = (bookedForCustomer?.userDisplayName?.availablePaymentOptions ?? '')
		.split(',')
		.map(s => s.trim())
		.filter(Boolean);
	// Self-booking: the booked-for customer is the logged-in user, whose invoiced status is the
	// group-based store.isInvoicedUser. canInvoice falls back to that same flag for self-booking, so
	// keying off it here keeps the pre-select aligned — the fetched type options can be empty for a
	// customer invoiced via the legacy group/role rather than an explicit per-type option set.
	// A non-staff user can only ever be booking for themselves; we don't rely on the context userId
	// matching, because it's seeded once on init and can still be empty/stale at this point.
	const isSelfBooking = !store.isStaff || userId === store.userId;
	const customerIsInvoiced =
		customerPaymentOptions.includes('Invoice') ||
		!!bookedForCustomer?.userDisplayName?.invoicedUser ||
		(isSelfBooking && store.isInvoicedUser);
	const isCheckInPayment = paymentLocation === 'check-in';
	const isGiftCertificatePayment = paymentLocation === 'gift-certificate';
	const isAnytimeTicketPayment = paymentLocation === 'anytime-ticket';
	const isParkingPayment = paymentLocation === 'parking';

	const isBookingWizardPayment = paymentLocation === 'booking-wizard';
	// Owner self-pay on an already-unpaid booking. Unlike the wizard it has no
	// "only new bookings" eligibility requirement — the booking already exists and the
	// owner is just settling it, so methods are gated purely on the per-type set.
	const isCompletePayment = paymentLocation === 'complete-payment';
	// Settling a charter, which is its own place rather than a variety of complete-payment: a charter
	// is taken over a counter or a phone as often as it is invoiced, so EFTPOS belongs here without
	// the check-in context that gates it everywhere else.
	const isCharterPayment = paymentLocation === 'charter';

	// Read the role + app-config observables during render (not just inside the effect) so the
	// observer re-renders — and, because they're listed in the effect deps below, the permission
	// calc re-runs — when the user's roles or the app config finish loading. Both load
	// asynchronously; previously the calc read them only inside the effect without depping on them,
	// so if roles/config arrived after the effect's last run (e.g. after `canSkipPayment` had
	// already resolved) it never recomputed — an admin could end up never seeing "Send invoice".
	const isStaffUser = store.isStaff;
	const isManagerUser = store.isManager;
	const isInvoicedUser = store.isInvoicedUser;
	const staffCanSkipPayment = appConfigStore.staffCanSkipPayment;
	const unpaidBookingsEnabled = appConfigStore.UnpaidBookingsEnabled;
	const allowCashInWizard = appConfigStore.allowCashPaymentsInBookingWizard();
	const allowTerminalPayment = appConfigStore.allowTerminalPayments();
	const allowEftposPayment = appConfigStore.allowEftposPayments();
	const supportsTerminalPayments = paymentStore.capabilities.supportsTerminalPayments;

	// Calculate permissions in useEffect to handle async dependencies
	useEffect(() => {
		const calculatePermissions = () => {
			// The per-type set governs which customer-selectable methods the booked-for
			// customer sees (Credit card, Invoice, Pay later). Staff booking on behalf are
			// unrestricted and see any app-enabled method. An empty set means "no per-type
			// restriction" — fall back to legacy behaviour so a customer is never locked out.
			const customerData = bookedForCustomer;
			const isGuestCustomer = !!customerData?.isGuest;
			const allowedPaymentOptions = (customerData?.userDisplayName?.availablePaymentOptions ?? '')
				.split(',')
				.map(s => s.trim())
				.filter(Boolean);
			const hasTypeRestriction = allowedPaymentOptions.length > 0;
			const typeAllows = (token: string) => allowedPaymentOptions.includes(token);

			// Whether the booked-for customer is themselves an invoiced customer. The per-type set
			// is authoritative when present; otherwise fall back to the legacy invoiced flag (and,
			// for a customer acting on their own booking, the logged-in user's flag).
			const customerInvoiceAllowed = hasTypeRestriction
				? typeAllows('Invoice')
				: !!customerData?.userDisplayName?.invoicedUser || (isSelfBooking && isInvoicedUser);

			// Unified invoice rule, shared by the wizard and complete-payment flows so they behave
			// identically: managers may invoice for ANY customer; staff may invoice for any customer
			// when the "staff can skip payment" setting is on, and otherwise (like customers acting
			// on their own booking) only when the booked-for customer is an invoiced customer.
			const invoiceAllowedForActor =
				isManagerUser || (isStaffUser && staffCanSkipPayment) || customerInvoiceAllowed;

			// Whether the wizard path would permit invoice if it weren't for the "only new
			// bookings" eligibility gate. Split out so we can distinguish "not allowed at all"
			// from "allowed, but blocked because the transaction contains an already-paid
			// booking" — the latter is shown disabled with an explanation rather than hidden.
			const wizardInvoiceAllowedIgnoringEligibility = () => invoiceAllowedForActor;

			const shouldEnableInvoice = () => {
				if (isGuestCustomer) {
					// Guest bookings cannot be invoiced
					return false;
				}

				if (isAnytimeTicketPayment) {
					// Anytime tickets cannot be invoiced per user request
					return false;
				}

				if (isParkingPayment) {
					// Parking cannot be invoiced
					return false;
				}

				if (isGiftCertificatePayment) {
					return isManagerUser;
				}

				if (isCheckInPayment) {
					return canInvoiceOverride ?? false;
				}

				// Owner self-pay on an existing unpaid booking — no canSkipPayment gate. Uses the
				// same unified rule as the wizard: managers can invoice for anyone, staff only when
				// the owning customer is invoiced (the backend enforces the same per-type rule, so
				// showing it to staff otherwise produced a 403 → hang).
				if (isCompletePayment) {
					return invoiceAllowedForActor;
				}

				// A charter follows the same rule: managers can invoice anyone, staff only when the
				// charter's customer is an invoiced account — which is how most charters are settled.
				if (isCharterPayment) {
					return invoiceAllowedForActor;
				}

				// Wizard Logic
				const isEligible = canSkipPayment === true && !includeSubmitButtons;

				if (!isEligible) {
					return false;
				}

				return wizardInvoiceAllowedIgnoringEligibility();
			};

			const newCanInvoice = shouldEnableInvoice();

			// In the wizard, invoice is otherwise silently hidden when the transaction contains
			// a booking that has already been paid for (canSkipPayment === false). For a user
			// who could otherwise invoice, surface the option disabled with an explanation
			// instead. Only the wizard path has this eligibility gate.
			const isWizardInvoicePath =
				!isGuestCustomer &&
				!isAnytimeTicketPayment &&
				!isParkingPayment &&
				!isGiftCertificatePayment &&
				!isCheckInPayment &&
				!isCompletePayment &&
				!isCharterPayment;
			const hasPreviouslyPaidBooking = canSkipPayment === false;
			const newInvoiceDisabledForPaidBooking =
				isWizardInvoicePath &&
				!includeSubmitButtons &&
				hasPreviouslyPaidBooking &&
				wizardInvoiceAllowedIgnoringEligibility();

			// Pay later is driven by the booked-for customer's type: if their display name
			// includes the Pay later option, they may defer payment themselves — and staff
			// booking on their behalf may too. Failing that, staff+ may still defer payment when
			// the global AllowUnpaidBookings setting is on. A customer with no per-type
			// restriction (empty set) does not get pay later (they fall back to credit card like
			// a regular customer). Never offered for gift certificates, anytime tickets, parking,
			// guests, or when settling an already-unpaid booking (complete-payment) — you can't defer
			// payment on a booking that is already pending payment. Payment is taken for the
			// whole transaction at settlement, so staff are warned (see PaymentTypeSelection) to
			// use separate transactions when bookings need to be paid for separately.
			const customerAllowsPayLater = hasTypeRestriction && typeAllows('PayLater');
			const newCanPayLater =
				!isGiftCertificatePayment &&
				!isAnytimeTicketPayment &&
				!isParkingPayment &&
				!isGuestCustomer &&
				!isCompletePayment &&
				// Pay later splits a transaction per booking, and a charter has none. A charter for a
				// customer who is not invoiced is already created owing, so there is nothing to defer.
				!isCharterPayment &&
				(customerAllowsPayLater || (isStaffUser && unpaidBookingsEnabled));

			// Can take cash logic — same gating everywhere cash is offered (wizard, check-in,
			// parking, voucher/gift-certificate and anytime-ticket purchases): staff+ and the
			// "cash in booking wizard" app-config flag. The shared cash endpoint is
			// transaction-generic, so it settles any of these transaction types identically.
			const newCanTakeCash = isStaffUser && allowCashInWizard && !includeSubmitButtons;

			// Can pay by terminal logic. The override applies here too: a terminal payment is a card
			// payment taken on a reader, so anything a card cannot settle a reader cannot either.
			// Never for a charter: a charter is settled with a card entered here, not one presented at
			// a reader, which is a counter transaction the charter is rarely there for.
			const newCanByTerminal =
				(canPayByCardOverride ?? true)
				&& !isCharterPayment
				&& isStaffUser
				&& allowTerminalPayment
				&& supportsTerminalPayments
				&& !includeSubmitButtons;

			// Credit card is a universal payment method, available to every customer regardless of
			// their customer type's configured options — unless the thing being paid for cannot be
			// settled by card at all, which the caller says by setting the override.
			const newCanPayByCard = canPayByCardOverride ?? true;

			setCanInvoice(newCanInvoice);
			setCanPayLater(newCanPayLater);
			setCanTakeCash(newCanTakeCash);
			setCanPayByTerminal(newCanByTerminal);
			setCanPayByCard(newCanPayByCard);
			setInvoiceDisabledForPaidBooking(newInvoiceDisabledForPaidBooking);
		};

		calculatePermissions();
	}, [
		canPayByCardOverride,
		canSkipPayment,
		bookedForCustomer,
		isCharterPayment,
		isCheckInPayment,
		isGiftCertificatePayment,
		isAnytimeTicketPayment,
		isParkingPayment,
		isBookingWizardPayment,
		isCompletePayment,
		isSelfBooking,
		includeSubmitButtons,
		canInvoiceOverride,
		// Role + app-config inputs — recompute when they finish loading (fixes the race where an
		// admin could miss "Send invoice" because roles/config arrived after the last run).
		isStaffUser,
		isManagerUser,
		isInvoicedUser,
		staffCanSkipPayment,
		unpaidBookingsEnabled,
		allowCashInWizard,
		allowTerminalPayment,
		supportsTerminalPayments,
	]);

	const otherResponse = usePaymentAmount(
		eventBooking,
		inCheckIn,
		transactionId ?? undefined,
		undefined,
		triggerRecalculation,
	);

	useEffect(() => {
		if (otherResponse.type === 'data') {
			setTotalPrice(otherResponse?.data?.totalPrice ?? 0);
			setDynamicSurcharge(otherResponse.data?.creditCardSurcharge ?? 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [otherResponse?.type, otherResponse?.data?.totalPrice, otherResponse?.data?.creditCardSurcharge]);

	useEffect(() => {
		setDisableContinueButton(false);
	}, [paymentMethod]);

	// Invoiced customers (self-booking, or booked for by staff) lead with the pay-later section
	// and default to Send invoice (only when invoice is actually selectable).
	const defaultToInvoice = canInvoice && customerIsInvoiced;

	// Track explicit user selection so the invoice default below never overrides a manual choice.
	const userSelectedPaymentRef = useRef(false);

	// Invoiced customers default to Send invoice. The provider seeds the default to credit card, but
	// the exact timing varies (paymentMethod can still be undefined, or get re-seeded on init, when
	// defaultToInvoice first becomes true), so rather than matching a specific current value we
	// re-assert invoice whenever the flow isn't already on it — until the user picks a method
	// themselves. Scoped to the wizard / owner self-pay flows so the pay-now defaults of check-in
	// and parking are left alone.
	useEffect(() => {
		if (!(isBookingWizardPayment || isCompletePayment || isCharterPayment)) {
			return;
		}
		if (!defaultToInvoice || userSelectedPaymentRef.current) {
			return;
		}
		if (paymentMethod !== 'invoice-option') {
			setPaymentMethod('invoice-option');
		}
	}, [
		isBookingWizardPayment,
		isCompletePayment,
		isCharterPayment,
		defaultToInvoice,
		paymentMethod,
		setPaymentMethod,
	]);

	/*
	 * The provider seeds the selection to credit card, so a flow with card turned off would open on a
	 * method that isn't on screen — and submitting would take the card branch and fail. Move to
	 * whatever is actually offered instead.
	 */
	useEffect(() => {
		if (canPayByCard || paymentMethod !== 'credit-card-option') {
			return;
		}

		const firstAvailable: PaymentMethodId | undefined = canInvoice
			? 'invoice-option'
			: canTakeCash
				? 'cash-option'
				: canPayLater
					? 'pay-later'
					: undefined;

		if (firstAvailable) {
			setPaymentMethod(firstAvailable);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canPayByCard, canInvoice, canTakeCash, canPayLater, paymentMethod]);

	const togglePayment = (id?: PaymentMethodId) => {
		userSelectedPaymentRef.current = true;
		setPaymentMethod(id);
	};

	/*
	 * The checkout form, which hosts the card fields *and* owns `<form id={PAYMENT_FORM_ID}>` — the
	 * form every method submits through, card or not. Cash, EFTPOS, invoice and pay later all reach
	 * their endpoints from its submit handler.
	 *
	 * So it is defined once and mounted whether or not card is offered: inside the card option when it
	 * is, and clipped out of sight when it isn't. Without that, turning card off took the submit form
	 * with it and the pay button silently did nothing.
	 */
	const checkoutForm = (
		<PaymentProviderWrapper
			transactionId={transactionId}
			userId={userId}
			isAlteration={isAlteration}
			tripType={tripType}
			eventBooking={eventBooking}
			inCheckIn={isCheckInPayment}
			redirectUrl={transactionDetails?.formActionUrl}
			totalPrice={totalPrice ?? undefined}
			paymentType={paymentMethod ?? undefined}
			includeSubmitButtons={includeSubmitButtons}
			onCancelRedirectUrl={onCancelRedirectUrl}
			// Passed through so the wrapper can tell a failed amount fetch from one still in flight.
			// Both used to arrive as `totalPrice` 0 and render as an endless spinner.
			amountStatus={otherResponse.type}
			onRetryAmount={otherResponse.refresh}
			// Taken from the fetch, not from `totalPrice`: the context is written in an effect, so a
			// zero read from it during that gap is "not synced yet", not "nothing to pay". A response
			// with no payload at all is not a zero either — that is a malformed answer, and calling it
			// "nothing to pay" would tell the customer their booking is settled when it is not.
			amountIsZero={otherResponse.type === 'data' && otherResponse.data?.totalPrice === 0}
		/>
	);

	return (
		<div className="checkout-form-wrapper">
			{!isBookingWizardPayment && <h5>{isCheckInPayment ? 'Payment method' : 'Payment details'}</h5>}
			<div className="payment-types__wrap">
				<div className="total-amount__wrap">
					<div>
						<MessageBox status="warning" hidden={!showSurchargeModal} slim>
							{'Your selected payment method has updated the surcharge.'}
						</MessageBox>
						<MessageBox
							title={
								message?.includes('surcharge')
									? 'Surcharge updated'
									: 'Transaction could not be processed.'
							}
							status="error"
							hidden={isNullOrUndefined(message)}
							slim
						>
							{message ?? ''}
						</MessageBox>
					</div>
					<AmountPayable
						totalPrice={totalPrice ?? undefined}
						activePaymentMethod={paymentMethod ?? 'cash-option'}
						dynamicSurcharge={dynamicSurcharge ?? undefined}
					/>
					<PaymentTypeSelection
						disabled={paymentProcessing}
						canInvoice={canInvoice}
						invoiceDisabledForPaidBooking={invoiceDisabledForPaidBooking}
						payLaterSectionFirst={defaultToInvoice}
						canTakeCash={canTakeCash}
						canPayLater={canPayLater}
						canPayByTerminal={canPayByTerminal}
						canPayByCard={canPayByCard}
						showEftpos={
							allowEftposPayment && (isCheckInPayment || isCharterPayment || (isCompletePayment && inCheckIn))
						}
						activePaymentMethod={paymentMethod}
						togglePayment={togglePayment}
						// Saved cards are card payments too, so the override that turns card off has to reach
						// them — otherwise a purchase card cannot settle still offers "pay with card
						// ending in ...", and it fails at the provider after being chosen.
						canPayWithPreviousCards={store.isStaff && canPayByCard}
						userId={userId}
						isGuest={bookedForCustomer?.isGuest}
						cardPaymentMethod={
							<div
								className={classNames(
									'payment-option',
									paymentMethod === 'credit-card-option' ? 'active' : '',
									{ disabled: paymentProcessing },
								)}
								id="credit-card-option"
							>
								<div
									className="payment-option__header"
									onClick={() => togglePayment('credit-card-option')}
									onKeyDown={() => {}}
								>
									<div className="radio-button" />
									<div
										className="payment-option-label"
										style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
									>
										<FormattedMessage
											id="credit-card-payment-option__label"
											defaultMessage="Credit card"
										/>
										<SandboxBadge tooltip="Click for test card details." />
									</div>
								</div>
								<div className="payment-option-content">
									<div className="payment-info-text">{checkoutForm}</div>
								</div>
							</div>
						}
					/>
					{/*
					 * Clipped rather than display:none — the card fields mount inside this and a
					 * display:none container breaks that mounting. Nothing here is ever used when card
					 * is off; it is present only so the submit form exists.
					 */}
					{!canPayByCard && (
						<div className="sr-only" aria-hidden="true">
							{checkoutForm}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default observer(PaymentTypeWrapper);
